import { has } from "lodash";
import decode from "unescape";
import { getNextQuestion } from "./branching";
import useAppContext from "../components/AppContext";
import { useParams } from "react-router-dom";
import { MULTI_CHOICE_GROUPS } from "../config/tags";

export function dispatchEvent(event) {
  if (has(window, "dataLayer")) {
    window.dataLayer.push(event);
  }
}

export function setPageData(props) {
  if (!window.digital_data) {
    window.digital_data = {};
  }

  if (!window.digital_data.site_section) {
    window.digital_data.site_language = window.ww.gtmInfo.language;
    window.digital_data.site_section = "visitor";
    window.digital_data.page_category = "personal_assessment";
  }

  window.digital_data = {
    ...window.digital_data,
    ...props
  };
}

export function pageView(pageName) {
  setPageData({
    page_name: `asmt:${window.ww.locale.toLowerCase()}:${pageName}`
  });
  dispatchEvent({ event: "view_changed" });
}

export function signupPageView(pageName) {
  setPageData({
    page_name: `sign:${window.ww?.gtmInfo?.region}:${pageName}`
  });
  dispatchEvent({ event: "view_changed" });
}

/**
 * Return human-readable text from html
 */
function htmlToPlainText(label) {
  // Strip HTML tags.
  const sanitizedHTML = label.replace(/<[^>]+>/g, "");

  // Using NPM package unescape. Docs can be found here:
  // https://www.npmjs.com/package/unescape

  return decode(sanitizedHTML, "all");
}

/**
 * Format the da-label attribute
 * with specified length.
 */
export function formatLabelWithLength(
  length,
  label,
  deleteMultipleAnswerDelimeter = true
) {
  const regex = deleteMultipleAnswerDelimeter ? /:|;/gm : ":";

  // Grab the first length number of characters of the label.
  return label
    ? htmlToPlainText(label.replaceAll(regex, "")).slice(0, length)
    : "";
}

function getProfileAnswers(profile) {
  let answer = `gender: ${profile.gender ? profile.gender : "N/A"}`;
  answer += `; height: ${
    profile.imperial.height ? profile.imperial.height : "N/A"
  } ${profile.imperial.heightUnit}`;
  answer += `; current weight: ${
    profile.imperial.weight ? profile.imperial.weight : "N/A"
  } ${profile.imperial.weightUnit}`;
  answer += `; goal weight: ${
    profile.imperial.desiredWeight ? profile.imperial.desiredWeight : "N/A"
  } ${profile.imperial.weightUnit}`;

  return answer;
}

export function useNextButtonDaLabel(
  question,
  answer,
  nextQuestion,
  profile = null
) {
  const { questionPath } = useParams();
  const { assessment, nextStack } = useAppContext();

  let currentQuestionAnswer = "N/A";
  let nextQuestionSlug = "N/A";
  let nextQuestionText = "N/A";

  const pageWithoutAnswers =
    question?.isIntro ||
    question?.isProfile ||
    question?.isEmail ||
    (question.answers.presentationMetadata.length > 0 &&
      !question.answers.presentationMetadata[0].text);

  // Pages without answers: email, intro, profile and
  // question page without answers (to build intro page)
  if (pageWithoutAnswers) {
    const answers = [
      {
        questionId: question._id,
        answers: [
          {
            nextQuestionId:
              question.answers.presentationMetadata[0].nextQuestionId
          }
        ]
      }
    ];

    const nextQuestionOnPageWithoutAnswers = getNextQuestion(
      nextStack,
      questionPath,
      assessment,
      answers,
      true,
      true
    );

    if (nextQuestionOnPageWithoutAnswers) {
      nextQuestionSlug = nextQuestionOnPageWithoutAnswers.slug;
      nextQuestionText = nextQuestionOnPageWithoutAnswers.headerText1;
    } else {
      nextQuestionSlug = "N/A";
      nextQuestionText = "results page";
    }
  } else {
    // Page with answers

    // Question has at least 1 answer already.
    if (answer?.answers) {
      if (nextQuestion) {
        nextQuestionSlug = nextQuestion.slug;
        nextQuestionText = nextQuestion.headerText1;
      } else {
        nextQuestionSlug = "N/A";
        nextQuestionText = "results page";
      }

      if (
        question.answers.presentationType === "multipleChoice" ||
        question.tags.includes(MULTI_CHOICE_GROUPS)
      ) {
        currentQuestionAnswer = [];
        answer.answers.forEach(a => {
          currentQuestionAnswer.push(a.value);
        });

        currentQuestionAnswer = currentQuestionAnswer.join(";");
      } else {
        // Single choice.
        currentQuestionAnswer = answer.answers[0]?.text;
      }
    }
  }

  if (profile) {
    currentQuestionAnswer = getProfileAnswers(profile);
  }

  return `${formatLabelWithLength(
    100,
    question.headerText1
  )}:${formatLabelWithLength(
    100,
    currentQuestionAnswer,
    false
  )}:${formatLabelWithLength(100, nextQuestionSlug)}:${formatLabelWithLength(
    100,
    nextQuestionText
  )}`;
}
